/* eslint-disable no-underscore-dangle */
import {
  ElementNode,
  DOMExportOutput,
  NodeKey,
  RangeSelection,
  SerializedElementNode,
  Spread,
  ParagraphNode,
  $createParagraphNode,
} from 'lexical';

import { $createQuoteNode } from './helpers';

type SerializedCustomQuoteNode = Spread<
  {
    type: 'quote-node';
    version: 1;
    text: string;
    author: string;
  },
  SerializedElementNode
>;

export class QuoteNode extends ElementNode {
  __text: string;

  __author: string;

  static getType(): string {
    return 'quote-node';
  }

  static clone(node: QuoteNode): QuoteNode {
    return new QuoteNode(node.__text, node.__author, node.__key);
  }

  constructor(text: string, author: string, key?: NodeKey) {
    super(key);
    this.__text = text;
    this.__author = author;
  }

  // Геттеры/сеттеры для текста и автора
  setText(text: string) {
    const self = this.getWritable();
    self.__text = text;
  }

  getText(): string {
    return this.__text;
  }

  setAuthor(author: string) {
    const self = this.getWritable();
    self.__author = author;
  }

  getAuthor(): string {
    return this.__author;
  }

  // View
  createDOM(): HTMLElement {
    const element = document.createElement('blockquote');

    // Основные классы
    element.classList.add('editor-quote-node');
    element.contentEditable = 'false';

    // Создаем контейнер для текста
    const textContainer = document.createElement('span');
    textContainer.classList.add('editor-quote-text');
    textContainer.textContent = this.__text;
    element.appendChild(textContainer);

    // Создаем контейнер для автора
    if (this.__author) {
      const authorContainer = document.createElement('span');
      authorContainer.classList.add('editor-author-name');
      authorContainer.textContent = this.__author;
      element.appendChild(authorContainer);
    }

    return element;
  }

  updateDOM(): boolean {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('blockquote');
    element.className = 'editor-quote-node';

    const textContainer = document.createElement('p');
    textContainer.className = 'editor-quote-text';
    textContainer.textContent = this.__text;
    element.appendChild(textContainer);

    if (this.__author) {
      const authorContainer = document.createElement('p');
      authorContainer.className = 'editor-author-name';
      authorContainer.textContent = this.__author;
      element.appendChild(authorContainer);
    }

    return { element };
  }

  static importJSON(serializedNode: SerializedCustomQuoteNode): QuoteNode {
    return $createQuoteNode(serializedNode.text, serializedNode.author);
  }

  exportJSON(): SerializedCustomQuoteNode {
    return {
      ...super.exportJSON(),
      type: 'quote-node',
      version: 1,
      text: this.__text,
      author: this.__author,
    };
  }

  // Mutation
  insertNewAfter(_: RangeSelection, restoreSelection?: boolean): ParagraphNode {
    const newBlock = $createParagraphNode();
    const direction = this.getDirection();
    newBlock.setDirection(direction);
    this.insertAfter(newBlock, restoreSelection);
    return newBlock;
  }

  collapseAtStart(): true {
    const paragraph = $createParagraphNode();
    const children = this.getChildren();
    children.forEach((child) => paragraph.append(child));
    this.replace(paragraph);
    return true;
  }

  canMergeWhenEmpty(): true {
    return true;
  }

  getTextContent(): string {
    return `${this.__text} ${this.__author}`;
  }
}
